import type { RefCallback } from 'react';
import { useCallback, useRef, useState } from 'react';

/**
 * Track an element's rendered size (px) via ResizeObserver. Attach the returned
 * callback ref to the element; `size` is null until it has been measured. The
 * element should get its size from layout (e.g. `flex-1`), not from its children,
 * or a child sized from `size` would feed back into the measurement.
 */
export function useElementSize<T extends HTMLElement>(): [RefCallback<T>, { width: number; height: number } | null] {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (node == null) return;
    const r = node.getBoundingClientRect();
    setSize({ width: Math.round(r.width), height: Math.round(r.height) });
    const ro = new ResizeObserver(([entry]) => {
      if (entry == null) return;
      setSize({ width: Math.round(entry.contentRect.width), height: Math.round(entry.contentRect.height) });
    });
    ro.observe(node);
    observer.current = ro;
  }, []);

  return [ref, size];
}
